/**
 * The three geographies prices are aggregated over.
 *
 * Zooming alone used to decide this, which meant a city was only selectable in
 * a narrow band of zoom levels. Now zoom picks a sensible default and the user
 * can pin a level explicitly — you can stand at street zoom and still be
 * comparing whole cities.
 */
export type PriceLevel = "departement" | "commune" | "iris";
export type LevelSetting = "auto" | PriceLevel;

export interface LevelDef {
  id: PriceLevel;
  /** Shown in the level switcher. */
  label: string;
  /** Shown in the detail panel under the area name. */
  noun: string;
  /** Lowest zoom at which this level is the automatic choice. */
  fromZoom: number;
  source: string;
  sourceLayer: string;
  /**
   * Transparent fill used only for hit-testing. Separate from `fillId` because
   * clicking a zone has to keep working when the price colours are switched
   * off — `queryRenderedFeatures` only sees layers that are actually visible.
   */
  hitId: string;
  fillId: string;
  lineId: string;
  labelId: string;
  selectedId: string;
}

export const LEVELS: LevelDef[] = [
  {
    id: "departement",
    label: "Departments",
    noun: "Department",
    fromZoom: 0,
    source: "prices_departement",
    sourceLayer: "departements",
    hitId: "prices-dep-hit",
    fillId: "prices-dep-fill",
    lineId: "prices-dep-line",
    labelId: "prices-dep-label",
    selectedId: "prices-dep-selected",
  },
  {
    id: "commune",
    label: "Cities",
    noun: "City",
    fromZoom: 9.6,
    source: "prices_commune",
    sourceLayer: "communes",
    hitId: "prices-commune-hit",
    fillId: "prices-commune-fill",
    lineId: "prices-commune-line",
    labelId: "prices-commune-label",
    selectedId: "prices-commune-selected",
  },
  {
    id: "iris",
    label: "Neighbourhoods",
    noun: "Neighbourhood",
    // Deliberately high: neighbourhoods only become the useful unit once you
    // are close enough to tell one end of a city from the other.
    fromZoom: 12.8,
    source: "prices_iris",
    sourceLayer: "iris",
    hitId: "prices-iris-hit",
    fillId: "prices-iris-fill",
    lineId: "prices-iris-line",
    labelId: "prices-iris-label",
    selectedId: "prices-iris-selected",
  },
];

export const levelDef = (id: PriceLevel): LevelDef =>
  LEVELS.find((l) => l.id === id)!;

/** Which level a given zoom implies, when the user has not pinned one. */
export function levelForZoom(zoom: number): PriceLevel {
  let chosen: PriceLevel = "departement";
  for (const l of LEVELS) if (zoom >= l.fromZoom) chosen = l.id;
  return chosen;
}

/** The level actually in force, honouring a manual override. */
export function resolveLevel(setting: LevelSetting, zoom: number): PriceLevel {
  return setting === "auto" ? levelForZoom(zoom) : setting;
}
